import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  Calendar,
  ChevronLeft,
  ClipboardList,
  CreditCard,
  FileText,
  MessageCircle,
} from "lucide-react";
import { Button, Card, CardContent, Badge, cn } from "@cliently/ui";
import {
  type PortalTimelineItem,
  usePortalTimeline,
} from "../hooks/use-portal-timeline";
import { useOrgStore } from "../stores/org.store";



const kindIcon = (kind: PortalTimelineItem["kind"]) => {
  switch (kind) {
    case "appointment":
      return Calendar;
    case "invoice":
      return CreditCard;
    case "document":
      return FileText;
    case "request":
      return ClipboardList;
    default:
      return MessageCircle;
  }
}

const TIMELINE_KIND_BADGE_WIDTH_PX = 120;



const kindVariant = (kind: PortalTimelineItem["kind"]): "default" | "secondary" | "outline" => {
  switch (kind) {
    case "appointment":
      return "default";
    case "invoice":
      return "secondary";
    default:
      return "outline";
  }
}



export const TimelinePage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const { data: items = [], isLoading } = usePortalTimeline(activeClientId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2 h-8 px-2" asChild>
          <Link to="/dashboard">
            <ChevronLeft className="mr-1 h-4 w-4" />
            {t("timeline.backToDashboard", "Retour à l’accueil")}
          </Link>
        </Button>
        <div className="bg-muted h-10 max-w-md animate-pulse rounded-lg" />
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="bg-muted h-16 animate-pulse border-none" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" className="-ml-2 h-8 px-2" asChild>
        <Link to="/dashboard">
          <ChevronLeft className="mr-1 h-4 w-4" />
          {t("timeline.backToDashboard", "Retour à l’accueil")}
        </Link>
      </Button>
      <div>
        <h1 className="text-xl font-semibold tracking-tight">
          {t("timeline.title", "Historique")}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {t(
            "timeline.subtitle",
            "Vos rendez-vous, factures, documents, demandes et derniers messages, dans l’ordre chronologique.",
          )}
        </p>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            {t("timeline.empty", "Aucun événement pour le moment.")}
          </CardContent>
        </Card>
      ) : (
        <ol className="relative space-y-0 border-s border-border ps-6">
          {items.map((item, idx) => {
            const Icon = kindIcon(item.kind);
            const labelKind = t(`timeline.kind.${item.kind}`, item.kind);
            return (
              <li key={`${item.id}-${idx}`} className="mb-6 ms-2">
                <span className="bg-background ring-border absolute -start-1.5 mt-1.5 flex h-3 w-3 rounded-full ring-4" />
                <Link
                  to={item.href}
                  className={cn(
                    "focus-visible:ring-ring group -mx-2 flex flex-col gap-2 rounded-lg px-2 py-1 outline-none transition-colors hover:bg-muted/45 focus-visible:ring-2",
                  )}
                >
                  {item.orgName ? (
                    <span className="text-muted-foreground text-[10px] leading-5">
                      {item.orgName}
                    </span>
                  ) : null}
                  <time className="text-muted-foreground block text-xs">
                    {new Date(item.at).toLocaleString(locale, {
                      dateStyle: "medium",
                      timeStyle: item.kind === "appointment" ? "short" : undefined,
                    })}
                  </time>
                  <span className="text-primary block text-sm font-medium group-hover:underline">
                    {item.title}
                  </span>
                  {item.subtitle ? (
                    <p className="text-muted-foreground line-clamp-2 text-sm">
                      {item.subtitle}
                    </p>
                  ) : null}
                  <Badge
                    variant={kindVariant(item.kind)}
                    style={{
                      width: TIMELINE_KIND_BADGE_WIDTH_PX,
                      minWidth: TIMELINE_KIND_BADGE_WIDTH_PX,
                      maxWidth: TIMELINE_KIND_BADGE_WIDTH_PX,
                    }}
                    className="inline-flex h-7 shrink-0 items-center justify-center gap-1 px-2 text-[10px]"
                  >
                    <Icon className="h-3 w-3 shrink-0" />
                    <span className="min-w-0 truncate">{labelKind}</span>
                  </Badge>
                </Link>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
